import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../../shared/api/apiClient";
import ContactCustomerSelect from "./ContactCustomerSelect";
import "./OrderCartEditor.css";

const emptyItem = {
  category_id: "",
  sub_category_id: "",
  count: 1,
  custom_field_values: {},
};

const cartSignature = ({ customerName, description, status, items }) =>
  JSON.stringify({
    customerName,
    description,
    status,
    items: (items || []).map((item) => ({
      product_id: item.product_id || null,
      category_id: Number(item.category_id || 0),
      sub_category_id: Number(item.sub_category_id || 0),
      count: Number(item.count || 0),
      custom_field_values: item.custom_field_values || {},
    })),
  });

function OrderCartEditor({
  currency = "EUR",
  editingOrder = null,
  canCreate,
  canUpdate,
  onSaved,
  onCancelEdit,
  onDirtyChange,
}) {
  const { t, i18n } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [customerName, setCustomerName] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("pending");
  const [itemDraft, setItemDraft] = useState(emptyItem);
  const [items, setItems] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const baselineRef = useRef(
    cartSignature({ customerName: "", description: "", status: "pending", items: [] })
  );

  const formatMoney = (value) => {
    try {
      return new Intl.NumberFormat(i18n.resolvedLanguage || i18n.language || "en", {
        style: "currency",
        currency,
        maximumFractionDigits: 2,
      }).format(Number(value || 0));
    } catch (_error) {
      return `${Number(value || 0).toFixed(2)} ${currency}`;
    }
  };

  useEffect(() => {
    let active = true;
    const loadCatalog = async () => {
      setLoadingCatalog(true);
      try {
        const [categoryResponse, fieldsResponse] = await Promise.all([
          api.get("/auth/category/all"),
          api.get("/custom-fields/product"),
        ]);
        if (!active) return;
        setCategories(Array.isArray(categoryResponse.data) ? categoryResponse.data : []);
        setCustomFields(Array.isArray(fieldsResponse.data) ? fieldsResponse.data : []);
      } catch (_error) {
        if (active) setError(t("cart_catalog_load_failed"));
      } finally {
        if (active) setLoadingCatalog(false);
      }
    };
    loadCatalog();
    return () => {
      active = false;
    };
  }, [t]);

  useEffect(() => {
    if (!editingOrder) {
      baselineRef.current = cartSignature({
        customerName: "",
        description: "",
        status: "pending",
        items: [],
      });
      setCustomerName("");
      setDescription("");
      setStatus("pending");
      setItems([]);
      setItemDraft(emptyItem);
      return;
    }

    const initialCustomerName = editingOrder.customer_name || "";
    const initialDescription = editingOrder.description || "";
    const initialStatus = editingOrder.status || "pending";
    const initialItems = (editingOrder.items || []).map((item) => {
        const stock = Number(item.sub_category_obj?.count || 0);
        const booked = Number(item.sub_category_obj?.booked || 0);
        return {
          product_id: item.id,
          category_id: item.category_id,
          sub_category_id: item.sub_category_id,
          category_obj: item.category_obj,
          sub_category_obj: item.sub_category_obj,
          count: Number(item.count || 1),
          unit_price: Number(item.unit_price || 0),
          available: Math.max(stock - booked + Number(item.count || 0), 0),
          custom_field_values: item.custom_field_values || {},
        };
      });

    baselineRef.current = cartSignature({
      customerName: initialCustomerName,
      description: initialDescription,
      status: initialStatus,
      items: initialItems,
    });
    setCustomerName(initialCustomerName);
    setDescription(initialDescription);
    setStatus(initialStatus);
    setItems(initialItems);
  }, [editingOrder]);

  const selectedCategory = useMemo(
    () => categories.find((row) => String(row.id) === String(itemDraft.category_id)),
    [categories, itemDraft.category_id]
  );

  const selectedSubcategory = useMemo(
    () => subcategories.find((row) => String(row.id) === String(itemDraft.sub_category_id)),
    [subcategories, itemDraft.sub_category_id]
  );

  const visibleCustomFields = useMemo(
    () =>
      customFields.filter(
        (field) =>
          field.is_active !== false &&
          (field.category_id == null || String(field.category_id) === String(itemDraft.category_id))
      ),
    [customFields, itemDraft.category_id]
  );

  const cartTotal = useMemo(
    () => items.reduce((sum, item) => sum + Number(item.unit_price || 0) * Number(item.count || 0), 0),
    [items]
  );

  const totalQuantity = useMemo(
    () => items.reduce((sum, item) => sum + Number(item.count || 0), 0),
    [items]
  );

  useEffect(() => {
    const currentSignature = cartSignature({ customerName, description, status, items });
    onDirtyChange?.(currentSignature !== baselineRef.current);
  }, [customerName, description, items, onDirtyChange, status]);

  const translateName = (row) =>
    t(row?.name || "", { defaultValue: row?.fallback_name || row?.name || t("n_a") });

  const handleCategoryChange = async (event) => {
    const categoryId = event.target.value;
    setItemDraft({ ...emptyItem, category_id: categoryId });
    setSubcategories([]);
    setError("");
    if (!categoryId) return;
    try {
      const response = await api.get(`/auth/subcategories/by-category-id/${categoryId}`);
      setSubcategories(Array.isArray(response.data) ? response.data : []);
    } catch (_error) {
      setError(t("error_fetching_subcategories"));
    }
  };

  const handleCustomFieldChange = (key, value) => {
    setItemDraft((previous) => ({
      ...previous,
      custom_field_values: { ...previous.custom_field_values, [key]: value },
    }));
  };

  const renderCustomField = (field) => {
    const value = itemDraft.custom_field_values[field.key] ?? field.default_value ?? "";
    const label = t(field.label || field.label_key || field.key, {
      defaultValue: field.fallback_label || field.label || field.key,
    });

    if (field.data_type === "boolean") {
      return (
        <label className="orderCartCheckbox">
          <input
            type="checkbox"
            checked={value === true || value === "true" || value === "1"}
            onChange={(event) => handleCustomFieldChange(field.key, event.target.checked)}
          />
          <span>{label}</span>
        </label>
      );
    }

    if (field.data_type === "select") {
      const options = Array.isArray(field.options_json?.options)
        ? field.options_json.options
        : [];
      return (
        <select
          value={value}
          required={field.is_required}
          onChange={(event) => handleCustomFieldChange(field.key, event.target.value)}
        >
          <option value="">{t("select")}</option>
          {options.map((option) => (
            <option key={option} value={option}>
              {t(String(option), { defaultValue: String(option) })}
            </option>
          ))}
        </select>
      );
    }

    return (
      <input
        type={field.data_type === "number" ? "number" : field.data_type === "date" ? "date" : "text"}
        value={value}
        required={field.is_required}
        onChange={(event) => handleCustomFieldChange(field.key, event.target.value)}
      />
    );
  };

  const addItem = (event) => {
    event.preventDefault();
    setError("");
    if (!selectedCategory || !selectedSubcategory) {
      setError(t("cart_select_item_first"));
      return;
    }

    const quantity = Number(itemDraft.count || 0);
    if (quantity < 1) {
      setError(t("quantity_required"));
      return;
    }

    const stock = Number(selectedSubcategory.count || 0);
    const booked = Number(selectedSubcategory.booked || 0);
    const available = Math.max(stock - booked, 0);
    const existing = items.find(
      (item) => String(item.sub_category_id) === String(selectedSubcategory.id)
    );
    const nextQuantity = quantity + Number(existing?.count || 0);
    const availableForLine = existing ? Number(existing.available || 0) : available;
    if (nextQuantity > availableForLine) {
      setError(
        t("cart_insufficient_stock", {
          name: translateName(selectedSubcategory),
          available: availableForLine,
        })
      );
      return;
    }

    if (existing) {
      setItems((previous) =>
        previous.map((item) =>
          item === existing
            ? {
                ...item,
                count: nextQuantity,
                custom_field_values:
                  Object.keys(itemDraft.custom_field_values || {}).length > 0
                    ? itemDraft.custom_field_values
                    : item.custom_field_values,
              }
            : item
        )
      );
    } else {
      setItems((previous) => [
        ...previous,
        {
          category_id: Number(selectedCategory.id),
          sub_category_id: Number(selectedSubcategory.id),
          category_obj: selectedCategory,
          sub_category_obj: selectedSubcategory,
          count: quantity,
          unit_price: Number(selectedSubcategory.price_per_piece || 0),
          available,
          custom_field_values: itemDraft.custom_field_values,
        },
      ]);
    }

    setItemDraft(emptyItem);
    setSubcategories([]);
  };

  const updateQuantity = (index, quantity) => {
    setItems((previous) =>
      previous.map((item, itemIndex) =>
        itemIndex === index
          ? { ...item, count: Math.max(1, Math.min(Number(quantity || 1), item.available)) }
          : item
      )
    );
  };

  const removeItem = (index) => {
    setItems((previous) => previous.filter((_item, itemIndex) => itemIndex !== index));
  };

  const saveOrder = async () => {
    setError("");
    if (!customerName.trim()) {
      setError(t("cart_customer_required"));
      return;
    }
    if (items.length === 0) {
      setError(t("cart_empty_error"));
      return;
    }

    const payload = {
      customer_name: customerName.trim(),
      description,
      items: items.map((item) => ({
        product_id: item.product_id || null,
        category_id: Number(item.category_id),
        sub_category_id: Number(item.sub_category_id),
        count: Number(item.count),
        custom_field_values: item.custom_field_values || {},
      })),
    };
    if (editingOrder) payload.status = status;

    setSaving(true);
    try {
      if (editingOrder) {
        await api.put(`/auth/orders/${editingOrder.id}`, payload);
      } else {
        await api.post("/auth/orders", payload);
      }
      setItems([]);
      setCustomerName("");
      setDescription("");
      setStatus("pending");
      setItemDraft(emptyItem);
      onSaved?.(editingOrder ? "updated" : "created");
    } catch (requestError) {
      const detail = requestError?.response?.data?.detail;
      if (detail?.code === "insufficient_stock") {
        setError(
          t("cart_insufficient_stock", {
            name: detail.name || t("order_item"),
            available: detail.available,
          })
        );
      } else {
        setError(typeof detail === "string" ? detail : t("cart_save_failed"));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="orderCartEditor">
      <div className="orderCartCustomerGrid">
        <div className="orderCartField">
          <label>{t("customer_name")}</label>
          <ContactCustomerSelect
            value={customerName}
            onChange={setCustomerName}
            disabled={saving}
          />
        </div>
        <div className="orderCartField orderCartNoteField">
          <label>{t("order_note")}</label>
          <input
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder={t("order_note_placeholder")}
          />
        </div>
        {editingOrder && (
          <div className="orderCartField">
            <label>{t("status")}</label>
            <select value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="pending">{t("pending")}</option>
              <option value="completed">{t("completed")}</option>
              <option value="canceled">{t("canceled")}</option>
            </select>
          </div>
        )}
      </div>

      <form className="orderCartAddPanel" onSubmit={addItem}>
        <div className="orderCartPanelTitle">
          <span><i className="lni lni-plus" aria-hidden="true"></i></span>
          <div>
            <strong>{t("add_item_to_order")}</strong>
            <small>{t("add_item_to_order_hint")}</small>
          </div>
        </div>

        <div className="orderCartItemGrid">
          <div className="orderCartField">
            <label>{t("category")}</label>
            <select
              value={itemDraft.category_id}
              onChange={handleCategoryChange}
              disabled={loadingCatalog}
            >
              <option value="">{t("select_category")}</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>{translateName(category)}</option>
              ))}
            </select>
          </div>
          <div className="orderCartField">
            <label>{t("sub_category")}</label>
            <select
              value={itemDraft.sub_category_id}
              onChange={(event) => setItemDraft({ ...itemDraft, sub_category_id: event.target.value })}
              disabled={!itemDraft.category_id}
            >
              <option value="">{t("select_sub_category")}</option>
              {subcategories.map((subcategory) => (
                <option key={subcategory.id} value={subcategory.id}>
                  {translateName(subcategory)} · {t("available")}: {Math.max(Number(subcategory.count || 0) - Number(subcategory.booked || 0), 0)}
                </option>
              ))}
            </select>
          </div>
          <div className="orderCartField orderCartQuantityField">
            <label>{t("quantity")}</label>
            <input
              type="number"
              min="1"
              value={itemDraft.count}
              onChange={(event) => setItemDraft({ ...itemDraft, count: event.target.value })}
            />
          </div>
          <div className="orderCartPricePreview">
            <span>{t("unit_price")}</span>
            <strong>{formatMoney(selectedSubcategory?.price_per_piece)}</strong>
          </div>
          {visibleCustomFields.map((field) => (
            <div key={field.id || field.key} className="orderCartField">
              <label>
                {t(field.label || field.label_key || field.key, { defaultValue: field.label || field.key })}
                {field.is_required ? " *" : ""}
              </label>
              {renderCustomField(field)}
            </div>
          ))}
          <button className="orderCartAddBtn" type="submit" disabled={!selectedSubcategory}>
            <i className="lni lni-plus" aria-hidden="true"></i>
            {t("add_to_order")}
          </button>
        </div>
      </form>

      <section className="orderCartLines">
        <div className="orderCartLinesHeader">
          <div>
            <h4>{t("order_cart")}</h4>
            <p>{t("order_cart_hint")}</p>
          </div>
          <span>{t("cart_positions_count", { count: items.length })}</span>
        </div>

        {items.length === 0 ? (
          <div className="orderCartEmpty">
            <i className="lni lni-cart-1" aria-hidden="true"></i>
            <strong>{t("cart_is_empty")}</strong>
            <span>{t("cart_is_empty_hint")}</span>
          </div>
        ) : (
          <div className="orderCartLineList">
            {items.map((item, index) => (
              <article className="orderCartLine" key={`${item.sub_category_id}-${index}`}>
                <div className="orderCartLineName">
                  <span>{index + 1}</span>
                  <div>
                    <strong>{translateName(item.sub_category_obj)}</strong>
                    <small>{translateName(item.category_obj)}</small>
                  </div>
                </div>
                <div className="orderCartLinePrice">
                  <span>{t("unit_price")}</span>
                  <strong>{formatMoney(item.unit_price)}</strong>
                </div>
                <div className="orderCartQuantityControl">
                  <button type="button" onClick={() => updateQuantity(index, item.count - 1)}>−</button>
                  <input
                    type="number"
                    min="1"
                    max={item.available}
                    value={item.count}
                    onChange={(event) => updateQuantity(index, event.target.value)}
                    aria-label={t("quantity")}
                  />
                  <button type="button" onClick={() => updateQuantity(index, item.count + 1)}>+</button>
                  <small>{t("available")}: {item.available}</small>
                </div>
                <div className="orderCartLineTotal">
                  <span>{t("line_total")}</span>
                  <strong>{formatMoney(item.unit_price * item.count)}</strong>
                </div>
                <button
                  type="button"
                  className="orderCartRemoveBtn"
                  onClick={() => removeItem(index)}
                  title={t("remove_from_order")}
                  aria-label={t("remove_from_order")}
                >
                  <i className="lni lni-trash-3" aria-hidden="true"></i>
                </button>
              </article>
            ))}
          </div>
        )}
      </section>

      {error && <div className="orderCartError">{error}</div>}

      <div className="orderCartFooter">
        <div className="orderCartTotals">
          <span>{t("cart_summary", { positions: items.length, quantity: totalQuantity })}</span>
          <strong>{formatMoney(cartTotal)}</strong>
        </div>
        <div className="orderCartFooterActions">
          {editingOrder && (
            <button type="button" className="orderCartSecondaryBtn" onClick={onCancelEdit}>
              {t("cancel")}
            </button>
          )}
          <button
            type="button"
            className="orderCartSaveBtn"
            disabled={saving || items.length === 0 || (editingOrder ? !canUpdate : !canCreate)}
            onClick={saveOrder}
          >
            {saving
              ? t("saving")
              : editingOrder
                ? t("save_order_changes")
                : t("create_order_with_items", { count: items.length })}
          </button>
        </div>
      </div>
    </div>
  );
}

export default OrderCartEditor;
